import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { styled } from '@mui/material/styles';

import { UnstyledButton, UnstyledUnorderedList } from '../common/resetUI';
import Logout from '../user-profile/logout/logout';
import { getOrgIdInfo } from 'src/actions/auth';
import { updateQueryString } from 'src/actions/search';
import { setCurrentApplication } from 'src/actions/user';
import { displayOrg } from 'src/components/app/displayOrgUtil';
import { showSettings } from 'src/components/app/displaySettings';
import FuzzySearchModal from 'src/components/reusable-components/fuzzy-search/FuzzySearchModal';
import useCopyPasteSteps from 'src/hooks/solve/useCopyPasteSteps';
import { useAppDispatch } from 'src/store/hooks';
import { Apps, Routes, Tabs, UserRoles } from 'src/utils/enums';

type Props = {
  handleModalVisibility: () => void;
  role: UserRoles;
};

const PAGE_SETTING = 'settings';
const PAGE_ORG = 'org_settings';

const getUserRoleLabels = (t: TFunction<'app'>): Record<UserRoles, string> => ({
  [UserRoles.ROLE_ADMIN]: t('navbar.roles.admin'),
  [UserRoles.ROLE_SUPER_ADMIN]: t('navbar.roles.superAdmin'),
  [UserRoles.ROLE_USER]: t('navbar.roles.user'),
  [UserRoles.ROLE_AGENT]: t('navbar.roles.user'),
});

const NavbarMenu = ({ handleModalVisibility, role }: Props) => {
  const { t } = useTranslation('app');
  const [page, setPage] = useState(PAGE_SETTING);
  const [org, setOrg] = useState('');
  const { resetCopyPasteSteps } = useCopyPasteSteps();

  const dispatch = useAppDispatch();

  const fetchOrgId = useCallback(async () => {
    const data = await dispatch(getOrgIdInfo());
    const { organization_id } = data || {};
    if (organization_id) {
      setOrg(organization_id);
    }
  }, [dispatch]);

  useEffect(() => {
    if (role !== UserRoles.ROLE_SUPER_ADMIN) {
      fetchOrgId();
    }
  }, [role, fetchOrgId]);

  const orgInfo = useMemo(
    () => (
      <StyledOrg datat-testid='org-info'>
        <div>{displayOrg(role, org)}</div>
        <StyledRole>
          {getUserRoleLabels(t)[role] ??
            getUserRoleLabels(t)[UserRoles.ROLE_USER]}
        </StyledRole>
      </StyledOrg>
    ),
    [role, org, t],
  );

  const changeOrgOption = useMemo(
    () =>
      role === UserRoles.ROLE_SUPER_ADMIN && (
        <StyledSettings>
          <StyledButton
            data-testid='change-org'
            onClick={() => {
              setPage(PAGE_ORG);
              resetCopyPasteSteps();
            }}
          >
            {t('navbar.changeOrg')}
          </StyledButton>
        </StyledSettings>
      ),
    [role, resetCopyPasteSteps, t],
  );

  const settings = useMemo(
    () =>
      showSettings(role) && (
        <StyledSettings>
          <StyledLink
            data-testid='settings'
            onClick={() => {
              dispatch(setCurrentApplication(Apps.INSIGHTS, Tabs.SETTINGS));
              dispatch(updateQueryString(''));
            }}
            to={Routes.ACCOUNT_SETTINGS}
          >
            {t('navbar.settings')}
          </StyledLink>
        </StyledSettings>
      ),
    [dispatch, role, t],
  );

  const logout = useMemo(
    () => (
      <StyledSettings>
        <StyledLogout data-testid='logout' />
      </StyledSettings>
    ),
    [],
  );

  const orgSearchModal = useMemo(
    () => <StyledOrgSearch handleModalVisibility={handleModalVisibility} />,
    [handleModalVisibility],
  );

  const content = useMemo(() => {
    if (page === PAGE_SETTING) {
      return (
        <UnstyledUnorderedList>
          {orgInfo}
          {changeOrgOption}
          {settings}
          {logout}
        </UnstyledUnorderedList>
      );
    }

    if (page === PAGE_ORG) {
      return orgSearchModal;
    }
  }, [page, orgInfo, changeOrgOption, settings, logout, orgSearchModal]);

  return <StyledMenu data-testid='navbar-menu'>{content}</StyledMenu>;
};

const MemoizedNavbarMenu = memo(NavbarMenu);

export default MemoizedNavbarMenu;

const StyledMenu = styled('div')`
  --overlay-min-width: 240px;
  --item-padding: 12px 16px;

  border-radius: var(--border-radius-button);
  background: ${({ theme }) => theme.palette.background.default};
  box-shadow: 0 2px 4px var(--color-grey-100);
  color: var(--color-solid-white);
  min-width: var(--overlay-min-width);
  margin: 16px 24px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 40px;
  z-index: 1;
  user-select: none;
`;

const StyledRole = styled('sub')`
  font-size: var(--font-size-xs);
  opacity: 0.8;
`;

const StyledOrg = styled('li')`
  font-size: var(--font-size-xs);
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: var(--item-padding);
  font-weight: var(--font-weight-medium);
`;

const StyledSettings = styled('li')`
  border-bottom: 1px solid var(--color-grey-100);
  color: var(--color-text-primary);
  font-size: var(--font-size-xs);
  transition-duration: var(--duration-fast);
  &:last-child {
    border-bottom-width: 0;
  }
  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light};
  }
`;

const StyledLink = styled(Link)`
  display: block;
  text-decoration: none;
  padding: var(--item-padding);
  &:visited {
    color: var(--color-text-primary);
  }
`;

const StyledButton = styled(UnstyledButton)`
  padding: var(--item-padding);
  width: 100%;
  text-align: start;
`;

const StyledLogout = styled(Logout)`
  padding: var(--item-padding);
  width: 100%;
  text-align: start;
`;

const StyledOrgSearch = styled(FuzzySearchModal)`
  position: static;
`;
